import { type FC, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { IconArrowLeft, IconArrowRight } from '@redteclab/icons'

import { Button, FormHelperText } from 'base-ui'

import { useMarketplacePartnerRegisterPageFormCategoriesSelection } from '../../../hooks/useMarketplacePartnerRegisterPageFormCategoriesSelection'
import { useMarketplacePartnerRegisterPageContext } from '../../marketplacePartnerRegisterPageContext'

import { MarketplacePartnerRegisterPageFormCategoriesSelectionCard } from './MarketplacePartnerRegisterPageFormCategoriesSelectionCard'

export const MarketplacePartnerRegisterPageFormCategoriesSelection: FC = () => {
  const {
    availableCategories,
    categoriesSelectionEntries,
    handleCheck,
    moveCategories,
    selectedCategories,
  } = useMarketplacePartnerRegisterPageFormCategoriesSelection()

  const intl = useIntl()
  const { formState } = useMarketplacePartnerRegisterPageContext()
  const { model } = formState

  return (
    <div className="flex flex-col items-center justify-center gap-8">
      {model.validator.getFirstErrorFor('categories') && (
        <FormHelperText
          data-qa-id="global-MessageForm-categories-error-msg"
          error
        >
          {model.validator.getFirstErrorFor('categories')}
        </FormHelperText>
      )}
      <div className="flex flex-col items-center gap-8 tablet:flex-row">
        <MarketplacePartnerRegisterPageFormCategoriesSelectionCard
          data-qa-id="marketplacePartnerRegisterPage-card-availableCategories"
          entries={useMemo(
            () =>
              availableCategories.map(({ isChecked, key, label }) => ({
                isChecked,
                key,
                label,
              })),
            [availableCategories],
          )}
          id="marketplacePartnerRegisterPageCategoriesSelectionAvailable"
          label={intl.formatMessage({
            id: 'marketplacePartnerRegisterPage.categoriesSelection.available',
          })}
          onCheck={(value, checked) => {
            handleCheck(value, checked, true)
          }}
        />
        <div className="flex gap-2 tablet:flex-col">
          <Button
            aria-label={intl.formatMessage({
              id: 'marketplacePartnerRegisterPage.categories.categoriesSelection.moveToSelected.title',
            })}
            data-qa-id="sellerForm-button-moveToSelected"
            disabled={categoriesSelectionEntries.every(
              (entry) => !entry.isChecked || entry.isSelected,
            )}
            onClick={() => {
              moveCategories(true)
            }}
          >
            <IconArrowRight
              aria-hidden
              className="size-4 rotate-90 tablet:rotate-0"
            />
          </Button>
          <Button
            aria-label={intl.formatMessage({
              id: 'marketplacePartnerRegisterPage.categories.categoriesSelection.moveToAvailable.title',
            })}
            data-qa-id="sellerForm-button-moveToAvailable"
            disabled={categoriesSelectionEntries.every(
              (entry) => !entry.isChecked || !entry.isSelected,
            )}
            onClick={() => {
              moveCategories(false)
            }}
          >
            <IconArrowLeft
              aria-hidden
              className="size-4 rotate-90 tablet:rotate-0"
            />
          </Button>
        </div>
        <MarketplacePartnerRegisterPageFormCategoriesSelectionCard
          data-qa-id="marketplacePartnerRegisterPage-card-selectedCategories"
          entries={useMemo(
            () =>
              selectedCategories.map(({ isChecked, key, label }) => ({
                isChecked,
                key,
                label,
              })),
            [selectedCategories],
          )}
          id="marketplacePartnerRegisterPageCategoriesSelectionSelected"
          label={intl.formatMessage({
            id: 'marketplacePartnerRegisterPage.categoriesSelection.selected',
          })}
          onCheck={(value, checked) => {
            handleCheck(value, checked, false)
          }}
        />
      </div>
    </div>
  )
}
